/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { CSSProperties, ReactElement } from 'react';
import { cn } from '@/lib/utils';
import type { RefinementProps } from '../types';

// Brand swatch colors, keyed by the refinement value/label. Includes the French names used by the
// storefront (e.g. `bleu`, `rouge`) alongside common English aliases for resilience.
const colorMap: Record<string, string> = {
    // Reds & pinks
    rouge: '#db002f',
    red: '#db002f',
    rose: '#da3897',
    pink: '#da3897',
    // Oranges & yellows
    orange: '#f67f21',
    jaune: '#ffff00',
    yellow: '#ffff00',
    // Greens & blues
    vert: '#6aa345',
    green: '#6aa345',
    turquoise: '#38cdda',
    bleu: '#006092',
    blue: '#006092',
    // Purple
    violet: '#8708db',
    purple: '#8708db',
    // Neutrals & earth tones
    noir: '#000000',
    black: '#000000',
    blanc: '#ffffff',
    white: '#ffffff',
    gris: '#808080',
    grey: '#808080',
    gray: '#808080',
    marron: '#8b4513',
    brown: '#8b4513',
    beige: '#ceb565',
    // Metallics
    argent: '#cecece',
    silver: '#cecece',
    dore: '#ffd700',
    doré: '#ffd700',
    dor_: '#ffd700',
    gold: '#ffd700',
};

// Names that represent a mix of colors — rendered as a gradient instead of a single fill.
const multicolorNames = new Set(['multicolore', 'multicolor', 'multi']);
const multicolorBackground =
    'conic-gradient(from 90deg, #db002f, #f67f21, #ffff00, #6aa345, #38cdda, #006092, #8708db, #db002f)';

const mapColorNameToHex = (colorName: string): string | null => {
    const normalized = colorName.toLowerCase().trim();
    return colorMap[normalized] || null;
};

const isMulticolor = (colorName: string): boolean => multicolorNames.has(colorName.toLowerCase().trim());

const colorOptionButtonClass = cn(
    'flex min-w-0 items-center gap-1 rounded-none border-0 bg-transparent p-px text-left shadow-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
);

const swatchBaseClass = 'size-6 shrink-0 rounded-full border';
const swatchSelectedClass = cn(
    swatchBaseClass,
    'border-foreground ring-1 ring-foreground ring-offset-2 ring-offset-background'
);
const swatchUnselectedClass = cn(swatchBaseClass, 'border-input');

export default function RefineColor({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps): ReactElement {
    return (
        <div className="grid grid-cols-2 gap-2">
            {values.map((value) => {
                const color = mapColorNameToHex(value.value) || mapColorNameToHex(value.label || '');
                const isMulti = isMulticolor(value.value) || isMulticolor(value.label || '');
                const isSelected = isFilterSelected(attributeId, value.value);
                const label = value.label || value.value;
                const swatchStyle: CSSProperties = isMulti
                    ? { background: multicolorBackground }
                    : { backgroundColor: color ?? 'var(--muted)' };

                return (
                    <button
                        key={`${attributeId}:${value.value}`}
                        type="button"
                        onClick={() => toggleFilter(attributeId, value.value)}
                        className={colorOptionButtonClass}>
                        {/* 32px round swatch on the left, label + count inline to the right */}
                        <span
                            className={isSelected ? swatchSelectedClass : swatchUnselectedClass}
                            style={swatchStyle}
                        />

                        <span className="min-w-0 truncate text-sm text-foreground">
                            {label}
                            {value.hitCount !== undefined && ` (${value.hitCount})`}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
